// src/views/AccommodationProvider/ProviderRequestInspection.js
import React, { useState, useEffect } from 'react';
import { 
  Card, 
  CardBody, 
  CardTitle, 
  Badge, 
  Row, 
  Col, 
  Button, 
  Table,
  UncontrolledDropdown,
  DropdownToggle,
  DropdownMenu,
  DropdownItem,
  Alert,
  Spinner
} from 'reactstrap';
import { useNavigate } from 'react-router-dom';
import axiosInstance from '../../api/Axios';

const ProviderRequestInspection = () => {
  const navigate = useNavigate();
  
  // Get provider information from localStorage (set during login)
  const providerType = localStorage.getItem('provider_type');
  const providerName = localStorage.getItem('provider_name');
  const representativeName = localStorage.getItem('representative_name');
  const regStatus = localStorage.getItem('reg_status');
  const providerId = localStorage.getItem('provider_id');

  // State management
  const [properties, setProperties] = useState([]);
  const [loading, setLoading] = useState(true);
  const [alert, setAlert] = useState({ type: '', message: '', visible: false });

  // Determine welcome message based on provider type
  const getWelcomeMessage = () => {
    if (providerType === 'individual') {
      return `Request Property Inspection - ${providerName || 'Individual Provider'}`;
    } else if (providerType === 'entity') {
      return `Request Property Inspection - ${providerName || 'Your Organization'}`;
    } else {
      return 'Request Property Inspection';
    }
  };

  const getSubtitleMessage = () => {
    if (providerType === 'entity' && representativeName) {
      return `Representative: ${representativeName}`;
    }
    return null;
  };

  const getProviderTypeDisplay = () => {
    if (providerType === 'individual') {
      return 'Individual Property Owner';
    } else if (providerType === 'entity') {
      return 'Business/Organization';
    }
    return 'Accommodation Provider';
  };

  // Fetch properties with 'created' status
  useEffect(() => {
    const fetchProperties = async () => {
      if (!providerId || regStatus !== '2') {
        setLoading(false);
        return;
      }

      try {
        setLoading(true);
        const response = await axiosInstance.get(`/api/properties/provider/${providerId}/?status=created`);
        
        if (response.status === 200) {
          setProperties(response.data.properties || []);
        }
      } catch (error) {
        console.error('Error fetching properties:', error);
        setAlert({
          type: 'danger',
          message: 'Failed to load properties. Please try again.',
          visible: true
        });
      } finally {
        setLoading(false);
      }
    };

    fetchProperties();
  }, [providerId, regStatus]);

  const handleBackToPropertyManagement = () => {
    navigate('/provider-property-management');
  };

  const handleOwnership = (propertyId) => {
    navigate(`/provider-property-ownership/${propertyId}`);
  };

  const handleOperationalStandards = (propertyId) => {
    navigate(`/provider-operational-standards/${propertyId}`);
  };

  const handlePoliciesInsurance = (propertyId) => {
    navigate(`/provider-policies-insurances/${propertyId}`);
  };

  const handleBookInspection = (propertyId) => {
    navigate(`/provider-book-inspection/${propertyId}`);
  };

  const formatAddress = (property) => {
    const parts = [
      property.addresslines,
      property.suburb,
      property.city,
      property.province
    ].filter(Boolean);
    return parts.join(', ');
  };

  const getStatusBadge = (status) => {
    switch (status) {
      case 'created':
        return <Badge color="secondary">Created</Badge>;
      case 'inspection_requested':
        return <Badge color="warning">Inspection Requested</Badge>;
      case 'inspection_booked':
        return <Badge color="info">Inspection Booked</Badge>;
      case 'inspection_failed':
        return <Badge color="danger">Inspection Failed</Badge>;
      case 'inspection_passed':
        return <Badge color="success">Inspection Passed</Badge>;
      case 'active':
        return <Badge color="success">Active</Badge>;
      case 'inactive':
        return <Badge color="secondary">Inactive</Badge>;
      case 'suspended':
        return <Badge color="danger">Suspended</Badge>;
      default:
        return <Badge color="light">{status}</Badge>;
    }
  };

  return (
    <div className="container-fluid py-4">
      {/* Header Section */}
      <div className="row mb-4">
        <div className="col-12">
          <div className="d-flex justify-content-between align-items-center mb-3">
            <div>
              <h2 className="mb-1">{getWelcomeMessage()}</h2>
              {getSubtitleMessage() && (
                <p className="text-muted mb-1">{getSubtitleMessage()}</p>
              )}
              <Badge color="info" className="me-2">
                {getProviderTypeDisplay()}
              </Badge>
            </div>
            <Button 
              color="outline-secondary" 
              onClick={handleBackToPropertyManagement}
              className="btn-sm"
            >
              <i className="bi bi-arrow-left me-2"></i>
              Back to Property Management
            </Button>
          </div>
        </div>
      </div>

      {/* Alert */}
      {alert.visible && (
        <Row className="mb-3">
          <Col>
            <Alert 
              color={alert.type} 
              toggle={() => setAlert({ ...alert, visible: false })}
            >
              {alert.message}
            </Alert>
          </Col>
        </Row>
      )}

      {/* Properties Table */}
      <Row>
        <Col md="12">
          <Card>
            <CardBody>
              <CardTitle tag="h4" className="mb-4">
                <i className="bi bi-building me-2 text-primary"></i>
                Properties Ready for Inspection
              </CardTitle>
              
              {loading ? (
                <div className="text-center py-5">
                  <Spinner color="primary" />
                  <p className="mt-3 text-muted">Loading properties...</p>
                </div>
              ) : (
                <>
                  {properties.length === 0 ? (
                    <div className="text-center py-5">
                      <i className="bi bi-building-slash display-4 text-muted"></i>
                      <h5 className="mt-3 text-muted">No Properties Found</h5>
                      <p className="text-muted">
                        You don't have any properties with "Created" status that need inspection.
                      </p>
                      <Button 
                        color="primary" 
                        onClick={() => navigate('/provider-add-property')}
                      >
                        <i className="bi bi-plus-lg me-2"></i>
                        Add New Property
                      </Button>
                    </div>
                  ) : (
                    <div className="table-responsive" style={{ paddingBottom: '150px' }}>
                      <Table hover>
                        <thead>
                          <tr>
                            <th>Property Name</th>
                            <th>Address</th>
                            <th>Rooms</th>
                            <th>Beds</th>
                            <th>Status</th>
                            <th className="text-center">Actions</th>
                          </tr>
                        </thead>
                        <tbody>
                          {properties.map((property) => (
                            <tr key={property.id}>
                              <td>
                                <div className="fw-bold">
                                  {property.trading_name || `Property #${property.id}`}
                                </div>
                              </td>
                              <td>
                                <div>{formatAddress(property)}</div>
                                <small className="text-muted">
                                  {property.postcode}
                                </small>
                              </td>
                              <td className="text-center">
                                <span className="badge bg-light text-dark">
                                  {property.number_of_rooms || 0}
                                </span>
                              </td>
                              <td className="text-center">
                                <span className="badge bg-light text-dark">
                                  {property.number_of_beds || 0}
                                </span>
                              </td>
                              <td>
                                {getStatusBadge(property.status)}
                              </td>
                              <td className="text-center">
                                <UncontrolledDropdown>
                                  <DropdownToggle 
                                    color="light" 
                                    size="sm" 
                                    className="btn-outline-secondary"
                                    caret={false}
                                  >
                                    <i className="bi bi-three-dots"></i>
                                  </DropdownToggle>
                                  <DropdownMenu end>
                                    <DropdownItem 
                                      onClick={() => handleOwnership(property.id)}
                                    >
                                      <i className="bi bi-file-earmark-text me-2"></i>
                                      Ownership and Zoning
                                    </DropdownItem>
                                    <DropdownItem 
                                      onClick={() => handleOperationalStandards(property.id)}
                                    >
                                      <i className="bi bi-shield-check me-2"></i>
                                      Operational Standards
                                    </DropdownItem>
                                    <DropdownItem 
                                      onClick={() => handlePoliciesInsurance(property.id)}
                                    >
                                      <i className="bi bi-clipboard-data me-2"></i>
                                      Policies and Insurance
                                    </DropdownItem>
                                    <DropdownItem divider />
                                    <DropdownItem 
                                      onClick={() => handleBookInspection(property.id)}
                                      className="text-primary"
                                    >
                                      <i className="bi bi-calendar-check me-2"></i>
                                      Book Inspection
                                    </DropdownItem>
                                  </DropdownMenu>
                                </UncontrolledDropdown>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </Table>
                    </div>
                  )}
                </>
              )}
            </CardBody>
          </Card>
        </Col>
      </Row>

      {/* Help Section */}
      <Row className="mt-4">
        <Col md="12">
          <Card className="border-0 bg-light">
            <CardBody>
              <CardTitle tag="h6" className="mb-3">
                <i className="bi bi-info-circle me-2"></i>
                Inspection Process
              </CardTitle>
              <Row>
                <Col md="3">
                  <div className="mb-2">
                    <strong>1. Ownership Documents:</strong>
                  </div>
                  <p className="small text-muted">
                    Upload property ownership and zoning certificates before requesting inspection.
                  </p>
                </Col>
                <Col md="3">
                  <div className="mb-2">
                    <strong>2. Operational Standards:</strong>
                  </div>
                  <p className="small text-muted">
                    Ensure hygiene and OHS compliance certificates are current and uploaded.
                  </p>
                </Col>
                <Col md="3">
                  <div className="mb-2">
                    <strong>3. Policies & Insurance:</strong>
                  </div>
                  <p className="small text-muted">
                    Verify insurance coverage and policy compliance documentation.
                  </p>
                </Col>
                <Col md="3">
                  <div className="mb-2">
                    <strong>4. Book Inspection:</strong>
                  </div>
                  <p className="small text-muted">
                    Schedule your property inspection once all documents are uploaded.
                  </p>
                </Col>
              </Row>
            </CardBody>
          </Card>
        </Col>
      </Row>
    </div>
  );
};

export default ProviderRequestInspection;